import React, { useState, useCallback, useEffect, useRef } from 'react';
import QuizContent from '../shared/QuizContent';
import QuizTitle from '../shared/QuizTitle';
import QuizOptions from '../shared/QuizOptions';
import QuizOption from '../shared/QuizOption';
import PlayPauseIcon from '../shared/PlayPauseIcon';
import PersonalitySwiper from '../shared/PersonalitySwiper';
import stepSisSvg from '../../assets/StepSis.svg';
import stepMomSvg from '../../assets/StepMom.svg';
import girlfriendSvg from '../../assets/Girlfriend.svg';
import sexFriendSvg from '../../assets/SexFriend.svg';
import ssIcon from '../../assets/ss.svg';
import smIcon from '../../assets/sm.svg';
import gfIcon from '../../assets/gf.svg';
import { QUIZ_OPTIONS_DATA, getQuizOptionsData } from '../../constants';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import { loadCharacterAttributes, getAttributeId as getAttrId } from '../../utils/api.js';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackStep5 } from '../../utils/analytics';
import { useDesign } from '../../contexts/DesignContext';
import opacityHeart from '../../assets/valentinesDay/opacityHeart.png';
import iconRose from '../../assets/valentinesDay/iconRose.png';

const Step4 = ({ onAnswer, onPrevious, selections }) => {
  const { isNewDesign } = useDesign();
  const { isPersonality } = useGrowthBookValues();
  const location = useLocation();
  const audioRefs = useRef({});
  const playingVoiceRef = useRef(null);
  const sliderRef = useRef();

  const selectedStyle = location.pathname.includes('/create-ai-hentai')
    ? 'Anime'
    : location.pathname.includes('/create-ai-porn')
    ? 'Realistic'
    : selections?.welcome || 'Realistic';

  const voices = QUIZ_OPTIONS_DATA.voice;
  const personalities = QUIZ_OPTIONS_DATA.personality;
  // New visual personality set (Playful/Nympho/Submissive/Dominant). Used ONLY
  // when the createsexfriend-isPersonality flag is ON *and* we're NOT on NDG —
  // NDG keeps the original relationship block exactly as it was.
  const personalitiesV2 = getQuizOptionsData('personalityV2', selectedStyle);
  const usePersonalityV2 = !isNewDesign && isPersonality;

  const [selectedVoice, setSelectedVoice] = useState(voices[0]?.value || '');
  const [selectedPersonality, setSelectedPersonality] = useState(
    usePersonalityV2
      ? // Default to the SECOND personality (index 1) so it opens centered on
        // the card, matching the design — falls back to the first if absent.
        personalitiesV2[1]?.value || personalitiesV2[0]?.value || ''
      : personalities[0]?.value || '',
  );
  const [playingVoice, setPlayingVoice] = useState(null);
  const [attributesMap, setAttributesMap] = useState({});
  const [selectedAge, setSelectedAge] = useState(22);
  const isHalloween = !!location.pathname.includes('/halloween');

  const updateSliderBackground = (target, value) => {
    const min = Number(target.min);
    const max = Number(target.max);
    const percent = ((value - min) / (max - min)) * 100;
    if (isHalloween) {
      target.style.backgroundRepeat = 'no-repeat';
      target.style.backgroundImage = `linear-gradient(100.44deg, #ED712B 0%, #D03C3C ${percent}%, rgba(255,255,255,0.05) ${percent}%)`;
      target.style.backgroundSize = '100% 100%';
      return;
    }
    target.style.backgroundRepeat = 'no-repeat, no-repeat';
    target.style.backgroundImage = `repeating-linear-gradient(-42deg, #b39ddb 0px, #b39ddb 8px, #8b5cf6 12px, #8b5cf6 18px, #b39ddb 22px), linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.05) 100%)`;
    target.style.backgroundSize = `${percent}% 100%, 100% 100%`;
  };

  useEffect(() => {
    if (!sliderRef.current) return;
    updateSliderBackground(sliderRef.current, selectedAge);
  }, [selectedAge, isHalloween]);

  useEffect(() => {
    (async () => {
      const map = await loadCharacterAttributes();
      setAttributesMap(map);
    })();
  }, []);

  const stopAllAudio = useCallback(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    playingVoiceRef.current = null;
    setPlayingVoice(null);
  }, []);

  const handleVoiceClick = useCallback(
    (value) => {
      stopAllAudio();
      setSelectedVoice(value);
      const id = getAttrId('voice', value);
      console.log('📝 Voice answer:', value);
      console.log('🔗 API ID:', id);
    },
    [stopAllAudio],
  );

  // Personality V2 (non-NDG, flag ON) — visual selection; the API mapping to the
  // `personality` attribute happens in convertSelectionsToApiFormat.
  const handlePersonalityV2Select = useCallback((value) => {
    setSelectedPersonality(value);
  }, []);

  // Legacy relationship handler — used on NDG (kept exactly as before).
  const handlePersonalityClick = useCallback((value) => {
    setSelectedPersonality(value);
    const id = getAttrId('relationship', value);
    console.log('📝 Relationship answer:', value);
    console.log('🔗 API ID:', id);
  }, []);

  const PERSONALITY_ICONS = {
    stepSis: stepSisSvg,
    stepMom: stepMomSvg,
    girlfriend: girlfriendSvg,
    sexFriend: sexFriendSvg,
  };

  const NDG_PERSONALITY_ICONS = {
    stepSis: ssIcon,
    stepMom: smIcon,
    girlfriend: gfIcon,
    sexFriend: gfIcon,
  };

  const renderPersonalityIcon = (personalityValue) => {
    if (isNewDesign) {
      const src = NDG_PERSONALITY_ICONS[personalityValue];
      if (!src) return null;
      return (
        <img
          src={src}
          alt={personalityValue}
          style={{ width: '60px', height: '60px' }}
        />
      );
    }
    const src = PERSONALITY_ICONS[personalityValue];
    if (!src) return null;
    return (
      <div className="icon-badge">
        <img src={src} alt={personalityValue} />
      </div>
    );
  };

  const handlePlayVoice = useCallback(
    async (voice, event) => {
      event?.stopPropagation();

      setSelectedVoice(voice.value);

      const audio = audioRefs.current[voice.value];
      if (!audio) return;

      if (playingVoiceRef.current === voice.value) {
        stopAllAudio();
        return;
      }

      stopAllAudio();
      await new Promise((resolve) => setTimeout(resolve, 50));

      try {
        playingVoiceRef.current = voice.value;
        setPlayingVoice(voice.value);
        await audio.play();

        audio.onended = () => {
          playingVoiceRef.current = null;
          setPlayingVoice(null);
        };
      } catch (err) {
        console.log('Play interrupted:', err);
        playingVoiceRef.current = null;
        setPlayingVoice(null);
      }
    },
    [stopAllAudio],
  );

  const handleAgeChange = useCallback((event) => {
    const target = event.target;
    const value = Number(target.value);
    setSelectedAge(value);
    updateSliderBackground(target, value);
  }, [isHalloween]);

  const handleContinue = useCallback(() => {
    const voiceOption = voices.find((v) => v.value === selectedVoice);

    trackStep5(selectedVoice, selectedPersonality);

    onAnswer('step4', {
      voice: selectedVoice,
      voiceAudio: voiceOption?.audio || null,
      personality: selectedPersonality,
      ...(!isNewDesign && { age: selectedAge }),
    });
  }, [selectedVoice, selectedPersonality, selectedAge, isNewDesign, onAnswer]);

  const canContinue = selectedVoice && selectedPersonality;

  const { t } = useTranslation();

  return (
    <div className="quizContentWrapper">
      <img
        src={opacityHeart}
        alt=""
        className="step4-bottom-heart"
        aria-hidden="true"
      />
      <img
        src={iconRose}
        alt=""
        className="step4-icon-rose"
        aria-hidden="true"
      />
      <QuizContent
        className={`step4-design${
          usePersonalityV2 ? ' step4-design--personality' : ''
        }`}
      >
        {/* Personality swiper — ONLY on non-NDG when createsexfriend-isPersonality
            is ON. Goes ABOVE the voice block. (NDG keeps its original relationship
            block below the voice block — see further down.) */}
        {usePersonalityV2 && (
          <div className="step4-block step4-personality-block">
            <QuizTitle>
              {isHalloween ? (
                <>
                  {t('choose')}
                  <span className="specialText"> {t('personalityText')}</span>
                </>
              ) : (
                t('choose_personality')
              )}
            </QuizTitle>
            <PersonalitySwiper
              options={personalitiesV2}
              selectedValue={selectedPersonality}
              onSelect={handlePersonalityV2Select}
            />
          </div>
        )}

        {/* Voice Block */}
        <div className="step4-block">
          <QuizTitle>
            {isHalloween || isNewDesign ? (
              <>
                {t('choose')}
                <span className="specialText"> {t('voice_text')}</span>
              </>
            ) : (
              t('choose_voice')
            )}
          </QuizTitle>
          <QuizOptions className="voice-options">
            {voices.map((voice) => (
              <QuizOption
                key={voice.value}
                value={voice.value}
                name="voice"
                onClick={handleVoiceClick}
                className={`voice-option ${
                  selectedVoice === voice.value
                    ? 'selected'
                    : selectedVoice
                    ? 'not-selected'
                    : ''
                }`}
              >
                <div className="voice-card">
                  <div className="voice-text">
                    <div className="voice-title">{t(voice.text)}</div>
                  </div>
                  <div className="voice-controls">
                    <button
                      className="play-button"
                      onClick={(e) => handlePlayVoice(voice, e)}
                      type="button"
                    >
                      <PlayPauseIcon isPlaying={playingVoice === voice.value} />
                    </button>
                  </div>
                </div>
                <audio
                  ref={(el) => (audioRefs.current[voice.value] = el)}
                  src={voice.audio}
                  preload="metadata"
                />
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        {/* Relationships Block — NDG only, kept exactly as before. Non-NDG uses
            the personality swiper above (flag ON) or shows nothing (flag OFF). */}
        {isNewDesign && (
          <div className="step4-block">
            <QuizTitle>
              {isHalloween || isNewDesign ? (
                <>
                  {t('choose')}
                  <span className="specialText"> {t('relationships_text')}</span>
                </>
              ) : (
                t('choose_relationships')
              )}
            </QuizTitle>
            <QuizOptions className="voice-options">
              {personalities.map((personality) => (
                <QuizOption
                  key={personality.value}
                  value={personality.value}
                  name="personality"
                  onClick={handlePersonalityClick}
                  className={`personality-option ${
                    selectedPersonality === personality.value
                      ? 'selected'
                      : selectedPersonality
                      ? 'not-selected'
                      : ''
                  }`}
                >
                  <div className="personality-item">
                    <div className="personality-icon">
                      {renderPersonalityIcon(personality.value)}
                    </div>
                    <div className="personality-info">
                      <div className="personality-title">
                        {t(personality.text)}
                      </div>
                    </div>
                  </div>
                </QuizOption>
              ))}
            </QuizOptions>
          </div>
        )}

        {/* Age Block — for non-ndg routes */}
        {!isNewDesign && (
          <div className="step1-block">
            <QuizTitle>
              {isHalloween ? (
                <>
                  {t('choose')}
                  <span className="specialText"> {t('age_text')}</span>
                </>
              ) : (
                t('choose_age')
              )}
            </QuizTitle>
            <div className="age-selector">
              <div className="age-selector-block">
                <div className="age-slider-container">
                  <div className="age-slider-num">18+</div>
                  <div className="age-slider-wrapper">
                    <input
                      ref={sliderRef}
                      type="range"
                      min="18"
                      max="55"
                      value={selectedAge}
                      onChange={handleAgeChange}
                      className="age-slider"
                    />
                    <div
                      className="age-thumb-label"
                      style={{
                        left: `calc(${((selectedAge - 18) / (55 - 18)) * 100}% + ${(0.5 - (selectedAge - 18) / (55 - 18)) * 48}px)`,
                      }}
                    >
                      {selectedAge}
                    </div>
                  </div>
                  <div className="age-slider-num">55+</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="navigation-wrapper">
          <button className="previous-button" onClick={onPrevious}>
            {t('previous')}
          </button>
          <button
            className="continue-button"
            onClick={handleContinue}
            disabled={!canContinue}
          >
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  );
};

export default Step4;
